// src/components/admin/EditorEfectoEstacional.js
// Sección en tab Integraciones para activar/desactivar y configurar el efecto
// estacional del sitio público (nieve, estrellas). Solo SuperAdmin.
// Guarda en ConfiguracionTexto (clave EFECTO_ESTACIONAL) → queda historial de versiones.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Alert, Button, Grid, TextField, FormControlLabel,
  Switch, CircularProgress, Slider, MenuItem, Chip,
} from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SaveIcon from '@mui/icons-material/Save';
import { getConfiguracionTexto, actualizarConfiguracionTexto } from '../../services/configuracionTextoApi';
import { CLAVE_EFECTO, EFECTO_DEFAULT, normalizarEfecto, LienzoEfecto } from '../EfectoEstacional';

const TITULO = 'Efecto estacional del sitio público';

export default function EditorEfectoEstacional({ onToast }) {
  const [cfg, setCfg]             = useState(null);
  const [meta, setMeta]           = useState(null); // { versionActual, fechaModificacion, modificadoPor }
  const [noExiste, setNoExiste]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getConfiguracionTexto(CLAVE_EFECTO);
      setCfg(normalizarEfecto(t?.contenido));
      setMeta({ versionActual: t?.versionActual, fechaModificacion: t?.fechaModificacion, modificadoPor: t?.modificadoPor });
      setNoExiste(false);
    } catch (e) {
      if (e.status === 404) { setNoExiste(true); setCfg({ ...EFECTO_DEFAULT }); }
      else onToast?.(e.message || 'No se pudo cargar la configuración del efecto.', 'error');
    } finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const guardar = async (override) => {
    const datos = { ...cfg, ...(override || {}) };
    if (datos.desde && datos.hasta && datos.desde > datos.hasta) {
      onToast?.('La fecha "desde" no puede ser posterior a "hasta".', 'error');
      return;
    }
    setGuardando(true);
    try {
      const r = await actualizarConfiguracionTexto(CLAVE_EFECTO, {
        titulo: TITULO,
        contenido: JSON.stringify(normalizarEfecto(datos), null, 2),
      });
      onToast?.(r?.mensaje === 'Sin cambios.' ? 'Sin cambios.' : (datos.activo ? 'Efecto activado y guardado.' : 'Efecto desactivado.'));
      await cargar();
    } catch (e) {
      onToast?.(e?.data?.mensaje || e.message || 'No se pudo guardar.', 'error');
    } finally { setGuardando(false); }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <AcUnitIcon sx={{ color: '#2e7d32' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a4d2e' }}>Efecto estacional</Typography>
        {cfg && !noExiste && (
          <Chip size="small" label={cfg.activo ? 'ACTIVO' : 'INACTIVO'}
            sx={{ fontWeight: 700, background: cfg.activo ? '#e8f5e9' : '#eee', color: cfg.activo ? '#1a4d2e' : '#666' }} />
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Nieve o estrellas cayendo en el sitio público (no aparece en el panel ni en Mi portal).
        Los cambios se ven en el sitio sin redeploy, en la siguiente carga de página.
      </Typography>

      {loading && <CircularProgress size={20} />}

      {noExiste && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          La clave <strong>{CLAVE_EFECTO}</strong> no existe en la base de datos. Ejecuta el script
          <strong> ecolab_sql/26_efecto_estacional.sql</strong> y recarga esta sección.
        </Alert>
      )}

      {cfg && !loading && !noExiste && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <FormControlLabel
                  control={<Switch color="success" checked={cfg.activo} onChange={e => set('activo', e.target.checked)} />}
                  label={<Typography sx={{ fontWeight: 600 }}>Efecto activo</Typography>} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField select size="small" fullWidth label="Tipo" value={cfg.tipo}
                      onChange={e => set('tipo', e.target.value)}>
                      <MenuItem value="nieve">Nieve</MenuItem>
                      <MenuItem value="estrellas">Estrellas</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth type="color" label="Color" value={cfg.color}
                      onChange={e => set('color', e.target.value)} InputLabelProps={{ shrink: true }} />
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Intensidad (cantidad): {cfg.intensidad}</Typography>
                  <Slider value={cfg.intensidad} min={1} max={100} step={1} color="success"
                    onChange={(_, v) => set('intensidad', v)} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Velocidad: {cfg.velocidad}x</Typography>
                  <Slider value={cfg.velocidad} min={0.3} max={3} step={0.1} color="success"
                    onChange={(_, v) => set('velocidad', v)} />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth type="date" label="Mostrar desde (opcional)"
                      value={cfg.desde} onChange={e => set('desde', e.target.value)} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth type="date" label="Mostrar hasta (opcional)"
                      value={cfg.hasta} onChange={e => set('hasta', e.target.value)} InputLabelProps={{ shrink: true }} />
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={<Switch checked={cfg.soloInicio} onChange={e => set('soloInicio', e.target.checked)} />}
                  label="Solo en la página de inicio" />
                <FormControlLabel
                  control={<Switch checked={cfg.permitirOcultar} onChange={e => set('permitirOcultar', e.target.checked)} />}
                  label="Mostrar botón ❄ para que el visitante pueda ocultarlo" />
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Vista previa</Typography>
              <Box sx={{
                position: 'relative', height: 220, borderRadius: 2, overflow: 'hidden',
                background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)',
              }}>
                <LienzoEfecto config={cfg} contenido />
                <Typography sx={{ position: 'absolute', left: 16, bottom: 12, color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
                  Así se verá sobre el sitio
                </Typography>
              </Box>
              {meta?.versionActual && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Versión {meta.versionActual}
                  {meta.fechaModificacion && ` · ${new Date(meta.fechaModificacion).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}`}
                  {meta.modificadoPor && ` · ${meta.modificadoPor}`}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button variant="contained" color="success" onClick={() => guardar()} disabled={guardando}
              startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}>
              Guardar
            </Button>
            {cfg.activo && (
              <Button variant="outlined" color="error" disabled={guardando}
                onClick={() => { set('activo', false); guardar({ activo: false }); }}>
                Apagar ya
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
